/**
 * Formatting utilities for menu schedule display.
 * Used by MenuUnavailablePage (next-available countdown) and
 * MenuDirectoryPage (per-menu schedule hints).
 */

import {
  formatAbsoluteTimeInZone,
  formatWallTime,
  getIsoDateInTimeZone,
  getTimeZoneAbbreviation,
  getViewerTimeZone,
} from '@/utils/timezone'

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

function formatIsoDate(dateString: string): string {
  const [year, month, day] = dateString.split('-').map(Number)
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(year, month - 1, day)))
}

/**
 * Format an ISO datetime into a human-friendly "next available" string.
 * Examples: "Today at 9:00 AM", "Tomorrow at 11:00 AM", "Wednesday at 5:00 PM"
 */
export function formatNextAvailable(
  isoString: string,
  businessTimeZone?: string | null
): string {
  const date = new Date(isoString)
  const now = new Date()
  const viewerTimeZone = getViewerTimeZone()
  const effectiveTimeZone = businessTimeZone || viewerTimeZone
  const timeStr = formatAbsoluteTimeInZone(date, effectiveTimeZone)
  const viewerTimeStr = effectiveTimeZone === viewerTimeZone
    ? null
    : `${formatAbsoluteTimeInZone(date, viewerTimeZone)} your time`

  const todayIso = getIsoDateInTimeZone(now, effectiveTimeZone)
  const targetIso = getIsoDateInTimeZone(date, effectiveTimeZone)
  const tomorrow = new Date(now)
  tomorrow.setDate(now.getDate() + 1)
  const tomorrowIso = getIsoDateInTimeZone(tomorrow, effectiveTimeZone)

  if (targetIso === todayIso) return viewerTimeStr ? `Today at ${timeStr} (${viewerTimeStr})` : `Today at ${timeStr}`
  if (targetIso === tomorrowIso) return viewerTimeStr ? `Tomorrow at ${timeStr} (${viewerTimeStr})` : `Tomorrow at ${timeStr}`

  const diffDays = Math.floor((date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
  if (diffDays < 7) {
    const dayName = date.toLocaleDateString([], {
      weekday: 'long',
      timeZone: effectiveTimeZone,
    })
    return viewerTimeStr ? `${dayName} at ${timeStr} (${viewerTimeStr})` : `${dayName} at ${timeStr}`
  }

  const dateStr = date.toLocaleDateString([], {
    month: 'short',
    day: 'numeric',
    timeZone: effectiveTimeZone,
  })
  return viewerTimeStr ? `${dateStr} at ${timeStr} (${viewerTimeStr})` : `${dateStr} at ${timeStr}`
}

function formatDays(days: number[]): string {
  if (!days || days.length === 0) return ''
  if (days.length === 7) return ''
  const sorted = [...days].sort()
  const names = sorted.map((d) => DAY_NAMES[d])
  // Check for contiguous ranges
  if (sorted.length >= 3) {
    const isContiguous = sorted.every((d, i) => i === 0 || d === sorted[i - 1] + 1)
    if (isContiguous) return `${DAY_NAMES[sorted[0]]}\u2013${DAY_NAMES[sorted[sorted.length - 1]]}`
  }
  return names.join(', ')
}

/**
 * Build a compact schedule hint string for a menu card.
 * Returns null when there's nothing meaningful to display.
 *
 * Examples:
 *   "9:00 AM - 5:00 PM"
 *   "9:00 AM - 5:00 PM · Mon-Fri"
 *   "9:00 AM - 5:00 PM · Mon-Fri · Apr 1 - Apr 30"
 */
export function formatScheduleHint(menu: {
  schedule_available_from_time?: string | null
  schedule_available_until_time?: string | null
  schedule_days?: number[]
  schedule_valid_from?: string | null
  schedule_valid_until?: string | null
}, businessTimeZone?: string | null): string | null {
  const parts: string[] = []
  const timeZoneAbbr = getTimeZoneAbbreviation(new Date(), businessTimeZone || getViewerTimeZone())

  const fromT = menu.schedule_available_from_time
  const untilT = menu.schedule_available_until_time

  if (fromT && untilT) {
    parts.push(`${formatWallTime(fromT)} \u2013 ${formatWallTime(untilT)} ${timeZoneAbbr}`)
  } else if (fromT) {
    parts.push(`From ${formatWallTime(fromT)} ${timeZoneAbbr}`)
  } else if (untilT) {
    parts.push(`Until ${formatWallTime(untilT)} ${timeZoneAbbr}`)
  }

  if (menu.schedule_days && menu.schedule_days.length > 0) {
    const dayStr = formatDays(menu.schedule_days)
    if (dayStr) parts.push(dayStr)
  }

  if (menu.schedule_valid_from && menu.schedule_valid_until) {
    const from = formatIsoDate(menu.schedule_valid_from)
    const until = formatIsoDate(menu.schedule_valid_until)
    parts.push(`${from} \u2013 ${until}`)
  }

  return parts.length > 0 ? parts.join(' \u00b7 ') : null
}
